import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as API from '../../apiServices/shakeApi';
import { getDrafts, saveDraft as persistDraft, removeDraft as dropDraft } from './utils/drafts';

const ShakeCtx = createContext(null);

export function ShakeProvider({ children, projectId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId],
  );

  const [view, setView] = useState('template'); // 'template' | 'job-form' | 'job-view'
  const [selectedJob, setSelectedJob] = useState(null); // for open/edit later

  // drafts
  const [drafts, setDrafts] = useState([]); // drafts for current template
  const [editingDraftId, setEditingDraftId] = useState(null);

  const loadDrafts = useCallback((templateId) => {
    setDrafts(getDrafts(templateId));
  }, []);

  const fetchTemplates = useCallback(async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      //const data = await API.getTemplates(); // returns array of templates (each can include jobs)
      const templateRequest = await API.getTemplates();
      const jobsRequest = await API.getJobsForProject(projectId);

      const templates = Array.isArray(templateRequest.response) ? templateRequest.response : [];
      const jobs = Array.isArray(jobsRequest.response) ? jobsRequest.response : [];
      templates.forEach((tpl) => {
        tpl.jobs = jobs.filter((job) => job.template_id === tpl.id);
      });
      setTemplates(templates);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchTemplates(projectId);
    }
  }, [fetchTemplates, projectId]);

  // Helper: compute earliest “doneAt” timestamp (ms since epoch) for all active jobs
  function getEarliestEstimatedCompletion(templates) {
    let earliest = null;
    const now = Date.now();

    for (const tpl of templates || []) {
      for (const job of tpl.jobs || []) {
        if (!job) continue;

        const status = job.status;
        if (status !== 'pending' && status !== 'in_progress') continue;

        const waitSec = Number(job.estimated_wait_time || 0);
        const procSec = Number(job.estimated_processing_time || 0);

        // If we have no estimate at all, skip
        if (!waitSec && !procSec) continue;

        const createdMs = Date.parse(job.created_time || job.createdTime || '');
        const startMs = Date.parse(job.startTime || job.start_time || '');

        let doneAt = null;

        if (status === 'pending') {
          // Pending: job is in queue; use created_time + (wait + processing)
          if (Number.isFinite(createdMs)) {
            doneAt = createdMs + (waitSec + procSec) * 1000;
          }
        } else if (status === 'in_progress') {
          // In progress: use startTime + processing, fallback to created_time + (wait + processing)
          if (Number.isFinite(startMs)) {
            doneAt = startMs + procSec * 1000;
          } else if (Number.isFinite(createdMs)) {
            doneAt = createdMs + (waitSec + procSec) * 1000;
          }
        }

        if (!doneAt) continue;

        if (earliest == null) {
          earliest = doneAt;
        } else {
          earliest = Math.min(earliest, doneAt);
        }
      }
    }

    return earliest;
  }

  useEffect(() => {
    if (!projectId || !templates.length) return;
    if (view === 'job-form') return; // don't auto-refresh while editing a job

    const earliest = getEarliestEstimatedCompletion(templates);
    if (!earliest) return;

    const now = Date.now();
    let delay = earliest - now;

    // If we're already past the estimate, wait 10s before checking again
    if (delay <= 0) {
      delay = 10_000;
    }

    // Don't wait more than 60s between checks
    delay = Math.min(delay, 60_000);

    const id = setTimeout(() => {
      fetchTemplates(projectId);
    }, delay);

    return () => clearTimeout(id);
  }, [templates, fetchTemplates, projectId, view]);

  const openForTemplate = useCallback((templateId) => {
    setSelectedTemplateId(templateId);
    setSelectedJob(null);
    setEditingDraftId(null);
    setView('template');
    //loadDrafts(tpl.id);
    setModalOpen(true);
  }, []);

  // Actions for view changes
  const startCreateNew = useCallback(() => {
    setSelectedJob(null);
    setEditingDraftId(null);
    setView('job-form');
  }, []);

  const openJob = useCallback((job) => {
    setSelectedJob(job);
    setEditingDraftId(null);
    setView('job-view'); // read-only mode
  }, []);

  const createJob = useCallback(
    async ({ name, formats, fields }) => {
      if (!selectedTemplate) return;
      try {
        const jobData = {
          template_id: selectedTemplate.id,
          name,
          formats,
          fields,
        };

        const jobResponse = await API.createJob(jobData);

        if (jobResponse?.response?.job_id) {
          // Successfull request
          const created = await API.getJob(jobResponse.response.job_id);

          // Update templates state to include new job
          setTemplates((prev) =>
            prev.map((t) =>
              t.id === selectedTemplate.id
                ? { ...t, jobs: [created.response, ...(t.jobs || [])] }
                : t,
            ),
          );
        } else {
          throw new Error('Invalid response from server');
        }

        // if we were editing a draft, remove it
        if (editingDraftId) {
          dropDraft(selectedTemplate.id, editingDraftId);
          setEditingDraftId(null);
          setDrafts(getDrafts(selectedTemplate.id));
        }
        return { success: true };
      } catch (e) {
        console.error(e);
        return {
          success: false,
          error: 'An error occurred while creating the job.',
        };
        // keep user on the form to fix issues
      }
    },
    [selectedTemplate, editingDraftId],
  );

  const createCopyFrom = useCallback((job) => {
    setSelectedJob({
      name: `${job.name} (copy)`,
      formats: job.formats || [],
      fields: job.fields || {},
      status: 'draft',
    });
    setEditingDraftId(null);
    setView('job-form');
  }, []);

  const openDraft = useCallback((draft) => {
    // Draft shape is { id, name, formats, fields, ... }
    setSelectedJob({
      id: draft.id,
      name: draft.name,
      formats: draft.formats,
      fields: draft.fields,
      status: 'draft',
    });
    setEditingDraftId(draft.id);
    setView('job-form');
  }, []);

  const saveDraft = useCallback(
    ({ name, formats, fields }) => {
      if (!selectedTemplate) return null;
      const saved = persistDraft(selectedTemplate.id, {
        id: editingDraftId || undefined,
        name,
        formats,
        fields,
      });
      setEditingDraftId(saved.id);
      setDrafts(getDrafts(selectedTemplate.id));
      return saved;
    },
    [selectedTemplate, editingDraftId],
  );

  const removeDraft = useCallback(
    (draftId) => {
      if (!selectedTemplate) return;
      dropDraft(selectedTemplate.id, draftId);
      setDrafts(getDrafts(selectedTemplate.id));
    },
    [selectedTemplate],
  );

  const headerTitle = useMemo(() => {
    if (!selectedTemplate) return '';
    if (view === 'job-form' || view === 'job-view') return selectedJob?.name || 'New version';
    return selectedTemplate.name;
  }, [selectedTemplate, selectedJob, view]);

  const value = useMemo(
    () => ({
      templates,
      loading,
      error,
      refetch: fetchTemplates,
      projectId,
      selectedTemplate,
      setSelectedTemplateId,
      selectedJob,
      setSelectedJob,
      isDropdownOpen,
      setDropdownOpen,
      isModalOpen,
      setModalOpen,
      view,
      setView,
      // actions
      openForTemplate,
      startCreateNew,
      openJob,
      createCopyFrom,
      openDraft,
      loadDrafts,
      saveDraft,
      removeDraft,
      goBack: () => setView('template'),
      createJob,
      // ui
      headerTitle,
      drafts,
      editingDraftId,
    }),
    [
      templates,
      loading,
      error,
      fetchTemplates,
      projectId,
      selectedTemplate,
      selectedJob,
      isDropdownOpen,
      isModalOpen,
      view,
      openForTemplate,
      startCreateNew,
      openJob,
      createJob,
      headerTitle,
      drafts,
      editingDraftId,
      createCopyFrom,
      openDraft,
      loadDrafts,
      saveDraft,
      removeDraft,
    ],
  );

  return <ShakeCtx.Provider value={value}>{children}</ShakeCtx.Provider>;
}

export function useShake() {
  const ctx = useContext(ShakeCtx);
  if (!ctx) throw new Error('useShake must be used within <ShakeProvider>');
  return ctx;
}
